import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import './ProgressActivityChart.css';

const RANGES = [
    { id: 7, label: '7D' },
    { id: 30, label: '30D' },
    { id: 90, label: '90D' },
];

const LAYOUT_DESKTOP = {
    w: 1360,
    h: 480,
    pad: { top: 22, right: 14, bottom: 34, left: 44 },
};

/** Compact user-space so labels/strokes stay readable on a ~360px phone. */
const LAYOUT_MOBILE = {
    w: 360,
    h: 260,
    pad: { top: 14, right: 10, bottom: 28, left: 28 },
};

/** Daily action bands (Action Tracker: streak = 3, hard cap = 15). */
const DAILY_STREAK_TARGET = 3;
const DAILY_ACTION_CAP = 15;
const Y_GUIDES = [0, 3, 7, 15];

function mapPoints(list) {
    return (list || []).map((p) => ({
        date: p.date || p.Date,
        count: Number(p.count ?? p.Count) || 0,
    }));
}

function maxCount(points) {
    return (points || []).reduce((m, p) => Math.max(m, Number(p.count) || 0), 0);
}

function niceCeil(n) {
    if (n <= 0) return DAILY_STREAK_TARGET;
    const padded = n * 1.12;
    const steps = [1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 15, 20, 25, 30, 40, 50, 75, 100];
    return steps.find((s) => s >= padded) || Math.ceil(padded);
}

function resolveMaxY(currentMax, previousMax, isMobile) {
    if (!isMobile) {
        return Math.max(DAILY_ACTION_CAP, currentMax, previousMax);
    }
    // Prefer current-period scale so small day counts stay visible.
    // Cap how much prior spikes can expand the axis (they may clip at top).
    const priorCap = Math.max(currentMax * 2.2, DAILY_STREAK_TARGET);
    const focus = Math.max(
        currentMax,
        previousMax > 0 ? Math.min(previousMax, priorCap) : 0
    );
    return niceCeil(Math.max(focus, DAILY_STREAK_TARGET));
}

function pointXY(points, index, maxY, layout) {
    const { w, h, pad } = layout;
    const innerW = w - pad.left - pad.right;
    const innerH = h - pad.top - pad.bottom;
    const stepX = points.length > 1 ? innerW / (points.length - 1) : 0;
    const raw = Number(points[index]?.count) || 0;
    const v = Math.min(raw, maxY);
    const x = pad.left + index * stepX;
    const y =
        pad.top + innerH - (maxY <= 0 ? 0 : (v / maxY) * innerH);
    return { x, y, raw, clipped: raw > maxY };
}

function buildPath(points, maxY, layout) {
    if (!points.length) return '';
    return points
        .map((_, i) => {
            const { x, y } = pointXY(points, i, maxY, layout);
            return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(' ');
}

function buildArea(points, maxY, layout) {
    if (!points.length) return '';
    const line = buildPath(points, maxY, layout);
    const { w, h, pad } = layout;
    const innerW = w - pad.left - pad.right;
    const baseY = h - pad.bottom;
    const stepX = points.length > 1 ? innerW / (points.length - 1) : 0;
    const lastX = pad.left + (points.length - 1) * stepX;
    return `${line} L ${lastX.toFixed(1)} ${baseY} L ${pad.left} ${baseY} Z`;
}

function sumCounts(points) {
    return points.reduce((s, p) => s + (Number(p.count) || 0), 0);
}

function avgCounts(points) {
    if (!points.length) return 0;
    return sumCounts(points) / points.length;
}

function splitWindow(series, range) {
    const all = series.slice(-(range * 2));
    const current = all.slice(-range);
    const previous = all.slice(0, Math.max(0, all.length - range)).slice(-range);
    return { current, previous };
}

function deltaPct(currentTotal, previousTotal) {
    if (previousTotal <= 0) return currentTotal > 0 ? 100 : 0;
    return Math.round(((currentTotal - previousTotal) / previousTotal) * 100);
}

function useIsMobileChart(breakpoint = 767) {
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined'
            ? window.matchMedia(`(max-width: ${breakpoint}px)`).matches
            : false
    );

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
        const onChange = (e) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, [breakpoint]);

    return isMobile;
}

/**
 * Profile V2 — activity chart: actions + goals, current vs previous period.
 */
const ProgressActivityChart = ({ active = true }) => {
    const { checkUserSession } = useContext(AppContext);
    const { token, userId } = checkUserSession();
    const isMobile = useIsMobileChart(767);

    const [range, setRange] = useState(30);
    const [actionSeries, setActionSeries] = useState([]);
    const [goalSeries, setGoalSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');

    const layout = isMobile ? LAYOUT_MOBILE : LAYOUT_DESKTOP;

    useEffect(() => {
        if (!active || !token || !userId) return undefined;
        let cancelled = false;

        (async () => {
            setLoading(true);
            setErrorMsg('');
            const daysNeeded = Math.min(180, range * 2);
            const [activityRes, goalsRes] = await Promise.all([
                requestAxios({
                    url: `Dashboard/GetActionActivity/${userId}?days=${daysNeeded}`,
                    token,
                }),
                requestAxios({
                    url: `goals/GetUserGoalsGroupedByStatus/${userId}`,
                    token,
                }),
            ]);
            if (cancelled) return;

            if (activityRes.error || !activityRes.data) {
                setErrorMsg('Could not load activity history.');
                setActionSeries([]);
                setGoalSeries([]);
                setLoading(false);
                return;
            }

            const data = activityRes.data;
            const actions = mapPoints(
                data.actions || data.Actions || data.series || data.Series
            );
            setActionSeries(actions);

            let goals = mapPoints(data.goals || data.Goals);
            const goalsSum = sumCounts(goals);

            // Fallback: bucket completed goals from My Goals if activity
            // returned an empty goals series (older API / query miss).
            const completedList =
                goalsRes.data?.completed || goalsRes.data?.Completed || [];
            if (
                goalsSum === 0 &&
                !goalsRes.error &&
                Array.isArray(completedList) &&
                completedList.length > 0
            ) {
                const byDay = {};
                completedList.forEach((g) => {
                    const raw = g.completionDate || g.CompletionDate;
                    if (!raw) return;
                    const dt = new Date(raw);
                    if (Number.isNaN(dt.getTime())) return;
                    const key = `${dt.getFullYear()}-${String(
                        dt.getMonth() + 1
                    ).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
                    byDay[key] = (byDay[key] || 0) + 1;
                });

                if (actions.length) {
                    goals = actions.map((p) => ({
                        date: p.date,
                        count: byDay[p.date] || 0,
                    }));
                } else {
                    goals = Object.keys(byDay)
                        .sort()
                        .map((date) => ({ date, count: byDay[date] }));
                }
            }

            setGoalSeries(goals);
            setLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [active, token, userId, range]);

    const chart = useMemo(() => {
        const actions = splitWindow(actionSeries, range);
        const goals = splitWindow(goalSeries, range);
        const currentMax = Math.max(
            maxCount(actions.current),
            maxCount(goals.current)
        );
        const previousMax = Math.max(
            maxCount(actions.previous),
            maxCount(goals.previous)
        );
        const maxY = resolveMaxY(currentMax, previousMax, isMobile);
        const priorClipped = isMobile && previousMax > maxY;

        const actionsCurrentTotal = sumCounts(actions.current);
        const actionsPreviousTotal = sumCounts(actions.previous);
        const goalsCurrentTotal = sumCounts(goals.current);
        const goalsPreviousTotal = sumCounts(goals.previous);

        return {
            actions,
            goals,
            maxY,
            priorClipped,
            actionsCurrentTotal,
            actionsPreviousTotal,
            goalsCurrentTotal,
            goalsPreviousTotal,
            actionsDelta: deltaPct(actionsCurrentTotal, actionsPreviousTotal),
            goalsDelta: deltaPct(goalsCurrentTotal, goalsPreviousTotal),
        };
    }, [actionSeries, goalSeries, range, isMobile]);

    const paths = useMemo(() => {
        const { actions, goals, maxY } = chart;
        return {
            actionsCurrent: buildPath(actions.current, maxY, layout),
            actionsPrevious: buildPath(actions.previous, maxY, layout),
            actionsArea: buildArea(actions.current, maxY, layout),
            goalsCurrent: buildPath(goals.current, maxY, layout),
            goalsPrevious: buildPath(goals.previous, maxY, layout),
        };
    }, [chart, layout]);

    const yTicks = useMemo(() => {
        if (isMobile) {
            const ticks = [0];
            const mid = Math.round(chart.maxY / 2);
            if (mid > 0 && mid < chart.maxY) ticks.push(mid);
            if (!ticks.includes(chart.maxY)) ticks.push(chart.maxY);
            return ticks;
        }
        const guides = Y_GUIDES.filter((v) => v <= chart.maxY);
        if (!guides.includes(0)) guides.unshift(0);
        if (chart.maxY > DAILY_ACTION_CAP && !guides.includes(chart.maxY)) {
            guides.push(chart.maxY);
        }
        return guides;
    }, [chart.maxY, isMobile]);

    const xLabels = useMemo(() => {
        const current = chart.actions.current;
        if (current.length < 2) return [];
        const idxs =
            range <= 7 || isMobile
                ? [0, Math.floor(current.length / 2), current.length - 1]
                : [
                      0,
                      Math.floor(current.length / 3),
                      Math.floor((current.length * 2) / 3),
                      current.length - 1,
                  ];
        return idxs.map((i) => {
            const d = current[i]?.date;
            if (!d) return '';
            const dt = new Date(`${d}T12:00:00`);
            return dt.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
            });
        });
    }, [chart.actions.current, range, isMobile]);

    const markers = useMemo(() => {
        if (!isMobile) return { actions: [], goals: [] };
        const { actions, goals, maxY } = chart;
        const pick = (points) =>
            points
                .map((p, i) => {
                    if (!p.count) return null;
                    const pt = pointXY(points, i, maxY, layout);
                    return { ...pt, key: `${p.date}-${i}` };
                })
                .filter(Boolean);
        return {
            actions: pick(actions.current),
            goals: pick(goals.current),
        };
    }, [chart, isMobile, layout]);

    return (
        <section
            className={`dv2-pac${isMobile ? ' is-mobile' : ''}`}
            aria-label="Actions and goals activity"
        >
            <header className="dv2-pac__head">
                <div>
                    <h3 className="dv2-pac__title">Actions &amp; goals activity</h3>
                    <p className="dv2-pac__hint">
                        Per day · actions streak {DAILY_STREAK_TARGET} / max{' '}
                        {DAILY_ACTION_CAP} · goals completed
                    </p>
                    <div className="dv2-pac__legend" aria-hidden>
                        <span className="dv2-pac__leg is-actions">Actions</span>
                        <span className="dv2-pac__leg is-goals">Goals</span>
                        <span className="dv2-pac__leg is-prev">
                            Previous period (dashed)
                        </span>
                    </div>
                </div>
                <div
                    className="dv2-pac__ranges"
                    role="tablist"
                    aria-label="Time range"
                >
                    {RANGES.map((r) => (
                        <button
                            key={r.id}
                            type="button"
                            role="tab"
                            aria-selected={range === r.id}
                            className={`dv2-pac__range${
                                range === r.id ? ' is-active' : ''
                            }`}
                            onClick={() => setRange(r.id)}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </header>

            {loading ? (
                <p className="dv2-pac__status">Loading chart…</p>
            ) : errorMsg ? (
                <p className="dv2-pac__status is-error">{errorMsg}</p>
            ) : (
                <>
                    <div className="dv2-pac__stats">
                        <div>
                            <strong>{chart.actionsCurrentTotal}</strong>
                            <span>actions · this {range}D</span>
                        </div>
                        <div
                            className={`dv2-pac__delta${
                                chart.actionsDelta >= 0 ? ' is-up' : ' is-down'
                            }`}
                        >
                            <strong>
                                {chart.actionsDelta > 0 ? '+' : ''}
                                {chart.actionsDelta}%
                            </strong>
                            <span>actions vs prior</span>
                        </div>
                        <div>
                            <strong>{chart.goalsCurrentTotal}</strong>
                            <span>goals · this {range}D</span>
                        </div>
                        <div
                            className={`dv2-pac__delta${
                                chart.goalsDelta >= 0 ? ' is-up' : ' is-down'
                            }`}
                        >
                            <strong>
                                {chart.goalsDelta > 0 ? '+' : ''}
                                {chart.goalsDelta}%
                            </strong>
                            <span>goals vs prior</span>
                        </div>
                        <div>
                            <strong>
                                {avgCounts(chart.actions.current).toFixed(1)}
                            </strong>
                            <span>avg actions / day</span>
                        </div>
                        <div>
                            <strong>
                                {avgCounts(chart.goals.current).toFixed(1)}
                            </strong>
                            <span>avg goals / day</span>
                        </div>
                    </div>

                    <div className="dv2-pac__chart-wrap">
                        <svg
                            className="dv2-pac__svg"
                            viewBox={`0 0 ${layout.w} ${layout.h}`}
                            role="img"
                            aria-label={`Actions and goals over the last ${range} days compared with the previous ${range} days`}
                        >
                            {yTicks.map((value) => {
                                const { h, pad } = layout;
                                const innerH = h - pad.top - pad.bottom;
                                const y =
                                    pad.top +
                                    innerH -
                                    (chart.maxY <= 0
                                        ? 0
                                        : (value / chart.maxY) * innerH);
                                const isGuide =
                                    !isMobile &&
                                    (value === DAILY_STREAK_TARGET ||
                                        value === DAILY_ACTION_CAP);
                                return (
                                    <g key={`y-${value}`}>
                                        <line
                                            x1={pad.left}
                                            x2={layout.w - pad.right}
                                            y1={y}
                                            y2={y}
                                            className={`dv2-pac__grid${
                                                isGuide ? ' is-guide' : ''
                                            }`}
                                        />
                                        <text
                                            x={pad.left - 6}
                                            y={y + 3}
                                            className="dv2-pac__ylabel"
                                            textAnchor="end"
                                        >
                                            {value}
                                        </text>
                                    </g>
                                );
                            })}

                            {paths.actionsPrevious ? (
                                <path
                                    d={paths.actionsPrevious}
                                    className="dv2-pac__line is-actions is-prev"
                                    fill="none"
                                />
                            ) : null}
                            {paths.goalsPrevious ? (
                                <path
                                    d={paths.goalsPrevious}
                                    className="dv2-pac__line is-goals is-prev"
                                    fill="none"
                                />
                            ) : null}
                            {paths.actionsArea ? (
                                <path
                                    d={paths.actionsArea}
                                    className="dv2-pac__area is-actions"
                                />
                            ) : null}
                            {paths.actionsCurrent ? (
                                <path
                                    d={paths.actionsCurrent}
                                    className="dv2-pac__line is-actions is-current"
                                    fill="none"
                                />
                            ) : null}
                            {paths.goalsCurrent ? (
                                <path
                                    d={paths.goalsCurrent}
                                    className="dv2-pac__line is-goals is-current"
                                    fill="none"
                                />
                            ) : null}

                            {markers.actions.map((m) => (
                                <circle
                                    key={`ma-${m.key}`}
                                    cx={m.x}
                                    cy={m.y}
                                    r={3.5}
                                    className="dv2-pac__dot is-actions"
                                />
                            ))}
                            {markers.goals.map((m) => (
                                <circle
                                    key={`mg-${m.key}`}
                                    cx={m.x}
                                    cy={m.y}
                                    r={3.5}
                                    className="dv2-pac__dot is-goals"
                                />
                            ))}

                            {xLabels.map((label, i) => {
                                const x =
                                    layout.pad.left +
                                    (i / Math.max(1, xLabels.length - 1)) *
                                        (layout.w -
                                            layout.pad.left -
                                            layout.pad.right);
                                return (
                                    <text
                                        key={`x-${i}`}
                                        x={x}
                                        y={layout.h - 8}
                                        className="dv2-pac__xlabel"
                                        textAnchor="middle"
                                    >
                                        {label}
                                    </text>
                                );
                            })}
                        </svg>
                    </div>
                    {chart.priorClipped ? (
                        <p className="dv2-pac__clip-note">
                            Prior-period peak scaled down so this week stays
                            readable.
                        </p>
                    ) : null}
                </>
            )}
        </section>
    );
};

export default ProgressActivityChart;
